import "./ManageUser.scss";

import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import AbunTable from "components/AbunTable/AbunTable";
import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { adminGetAIStatsPages, adminGetAIStatsPageData } from "../../utils/api";
import "./integration.scss";

export interface StatsPageData {
	id: number
	stats_id: string
	stats_topic: string
	stats_type: string
	created_on: string
	stats_link?: string
	// Admin-specific fields
	user_id?: number
	user_email?: string
	website_domain?: string
	website_name?: string
	user_plan?: string
}

interface AdminStatsResponse {
	status: string
	data: {
		pages: StatsPageData[]
		total_count: number
	}
}

export default function StatsPage(props: { 
	statsPages?: Array<StatsPageData>, 
	showDateColumn?: boolean, 
	isAdmin?: boolean,
	userId?: string,  
	searchTerm?: string 
}) {
	// ---------------------- STATE ----------------------
	const [showStatsPages, setStatsPages] = useState(false);
	const [adminStatsPages, setAdminStatsPages] = useState<StatsPageData[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [loadingRowId, setLoadingRowId] = useState<string | null>(null);

	// ---------------------- HOOKS ----------------------
	const navigate = useNavigate();

	// ---------------------- NON STATE CONSTANTS ----------------------
	const pageSizes = [10, 50, 150, 250, 500];
	
	// ---------------------- ADMIN API CALLS ----------------------
	const fetchAdminStatsPages = async (userId?: string, search?: string) => {
		setLoading(true);
		setError(null);
		
		try {
			// Use the API function instead of fetch
			const response = await adminGetAIStatsPages(userId, search);
			
			if (response.status === 200 && response.data?.status === 'success') {
				setAdminStatsPages(response.data.data.pages || []);
			} else {
				throw new Error(response.data?.message || 'Failed to fetch stats pages');
			}
		} catch (err: any) {
			console.error('Error fetching admin stats pages:', err);
			
			// Handle different error types
			let errorMessage = 'Failed to fetch stats pages';
			if (err.response?.data?.message) {
				errorMessage = err.response.data.message;
			} else if (err.message) {
				errorMessage = err.message;
			}
			
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	// ---------------------- ADMIN STATS PAGE DATA FETCH ----------------------
	const fetchAdminStatsPageData = async (statsId: string) => {
		setLoadingRowId(statsId);
		
		try {
			// Use the API function instead of fetch
			const response = await adminGetAIStatsPageData(statsId);
			
			if (response.status === 200 && response.data?.status === 'success' && response.data?.stats_data) {
				const statsData = response.data.stats_data;
				
				// Navigate to the stats page view 
				navigate(`/admin/view-stats-page/${statsData.stats_id}`, {
					state: {
						statsId: statsData.stats_id,
						statsType: statsData.stats_type,
						statsTopic: statsData.stats_topic,
						statsData: statsData,
						fromAdmin: true,
						navigationTimestamp: Date.now()
					},
					replace: false
				});
			} else {
				throw new Error(response.data?.message || 'Invalid response format');
			}
		} catch (err: any) {
			console.error('Error fetching admin stats page data:', err);
			
			// Handle different error types
			let errorMessage = 'Failed to fetch stats page data';
			if (err.response?.data?.message) {
				errorMessage = err.response.data.message;
			} else if (err.message) {
				errorMessage = err.message;
			}
			
			setError(errorMessage);
		} finally {
			setLoadingRowId(null);
		}
	};

	// ---------------------- EFFECTS ----------------------
	useEffect(() => {
		if (props.isAdmin && showStatsPages) {
			fetchAdminStatsPages(props.userId, props.searchTerm);
		}
	}, [props.isAdmin, showStatsPages, props.userId, props.searchTerm]);

	useEffect(() => {
		if (props.isAdmin && showStatsPages && props.userId) {
			fetchAdminStatsPages(props.userId, props.searchTerm);
		}
	}, [props.userId]);


	// ---------------------- HELPER FUNCTIONS ----------------------
	const truncateStatsId = (statsId: string, maxLength: number = 20) => {
		return statsId.length > maxLength ? `${statsId.substring(0, maxLength)}...` : statsId;
	};

	const formatDate = (dateString: string): string => {
		try {
			const date = new Date(dateString);
			
			// Get day with ordinal suffix
			const day = date.getDate();
			const getOrdinalSuffix = (day: number): string => {
				if (day > 3 && day < 21) return 'th';
				switch (day % 10) {
					case 1: return 'st';
					case 2: return 'nd';
					case 3: return 'rd';
					default: return 'th';
				}
			};
			
			// Get month abbreviation
			const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
			const month = months[date.getMonth()];
			
			// Get year (last 2 digits)
			const year = date.getFullYear().toString().slice(-2);
			
			// Get time in 12-hour format
			const hours = date.getHours();
			const minutes = date.getMinutes().toString().padStart(2, '0');
			const ampm = hours >= 12 ? 'PM' : 'AM';
			const displayHours = hours % 12 || 12;
			
			return `${day}${getOrdinalSuffix(day)} ${month}, ${year}, ${displayHours}:${minutes} ${ampm}`;
		} catch (error) {
			console.error('Error formatting date:', error);
			return dateString; 
		}
	};

	// ---------------------- HANDLERS ----------------------
	const handleViewStatsPage = (statsPage: StatsPageData) => {
		if (!statsPage.stats_id) {
			console.error("Invalid stats page data");
			return;
		}
		
		fetchAdminStatsPageData(statsPage.stats_id.trim());
	};

	// ---------------------- TABLE COLUMN DEFS ----------------------
	const columnHelper = createColumnHelper<StatsPageData>();
	const columnDefs: ColumnDef<StatsPageData, any>[] = [
		columnHelper.accessor((row: StatsPageData) => row.stats_topic, {
			id: 'stats_title',
			header: "Stats Title",
			cell: props => {
				const original = props.row.original;
				const isRowLoading = loadingRowId === original.stats_id;
				
				// For admin, make it clickable but don't use NavLink
				return (
					<div 
						style={{ 
							cursor: 'pointer', 
							color: '#007bff',
							opacity: isRowLoading ? 0.6 : 1,
							display: 'flex',
							alignItems: 'center'
						}}
						onClick={() => handleViewStatsPage(original)}
					>
						{isRowLoading && (
							<span style={{ marginRight: '8px' }}>Loading...</span>
						)}
						{original.stats_topic}
					</div>
				);
			},
			enableGlobalFilter: true,
			enableSorting: true
		}),

		columnHelper.accessor((row: StatsPageData) => row.stats_id, {
			id: 'stats_id',
			header: "Stats ID",
			cell: props => {
				const fullId = props.row.original.stats_id;
				const truncatedId = truncateStatsId(fullId);
				const isRowLoading = loadingRowId === fullId;
				
				return (
					<span 
						title={fullId} 
						style={{ 
							cursor: fullId.length > 20 ? 'help' : 'default',
							opacity: isRowLoading ? 0.6 : 1
						}}
					>
						{truncatedId}
					</span>
				);
			},
			enableGlobalFilter: true,
			enableSorting: true
		}),

		columnHelper.accessor((row: StatsPageData) => row.created_on, {
			id: 'created_on',
			header: "Created On",
			cell: info => {
				const isRowLoading = loadingRowId === info.row.original.stats_id;
				return (
					<span style={{ opacity: isRowLoading ? 0.6 : 1 }}>
						{formatDate(info.getValue())}
					</span>
				);
			},
			enableGlobalFilter: false,
		}),

		columnHelper.accessor((row: StatsPageData) => row.stats_type, {
			id: 'stats_type',
			header: "Stats Type",
			cell: info => {
				const value = info.getValue();
				const isRowLoading = loadingRowId === info.row.original.stats_id;
				return (
					<span style={{ opacity: isRowLoading ? 0.6 : 1 }}>
						{value ? value.charAt(0).toUpperCase() + value.slice(1) : "---"}
					</span>
				);
			},
			enableGlobalFilter: true,
			enableSorting: true
		}),
	];

	// Add stats link column last
	columnDefs.push(
		columnHelper.display({
			id: 'stats_link',
			header: "View Stats",
			cell: props => {
				const original = props.row.original;
				const isRowLoading = loadingRowId === original.stats_id;
				
				// For admin, use button that calls our handler
				return (
					<button 
						className="button is-primary"
						onClick={(e) => {
							e.stopPropagation(); 
							handleViewStatsPage(original);
						}}
						disabled={isRowLoading}
						style={{ 
							backgroundColor: isRowLoading ? "#86a5ec" : "",
							cursor: isRowLoading ? "not-allowed" : "pointer"
						}}
					>
						{isRowLoading ? "Loading..." : "View"}
					</button>
				);
			},
			enableGlobalFilter: false,
		})
	);

	// ---------------------- DATA SOURCE ----------------------
	const dataToUse = props.isAdmin ? adminStatsPages : (props.statsPages || []);

	// ---------------------- MAIN RENDER CODE ----------------------
	return (
		<div className={"card w-100 mt-4"}>
			<header
				className="card-header is-clickable"
				onClick={() => setStatsPages(!showStatsPages)}
			>
				<p className="card-header-title">
					Stats Pages 
				</p>
				<span className="card-header-icon">
					<FontAwesomeIcon icon={showStatsPages ? faChevronDown : faChevronRight} />
				</span>
			</header>
			{showStatsPages && (
				<div className={"card-content"}>
					<div className={"content"}>
						{error && (
							<div className="notification is-danger">
								<button className="delete" onClick={() => setError(null)}></button>
								{error}
							</div>
						)}
						
						<div className={"all-users-container"}>
							<AbunTable 
								tableContentName={"Stats Pages"}
								tableData={dataToUse}
								columnDefs={columnDefs}
								pageSizes={pageSizes}
								initialPageSize={pageSizes[0]}
								noDataText={props.isAdmin && loading ? "Loading..." : "No Stats Pages Found"}
								enableSorting={true}
								searchboxPlaceholderText={"Search stats pages..."} 
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}